import * as React from "react";
import { Alert, Badge, Button } from "@jhool-io/fe-components";
import { useNoteEditContext } from "../../context/notes.context";
import { goToMissingField } from "../../hooks/edit-note";

export function NoteValidationSummary() {
    const { missingFields } = useNoteEditContext();
    const [activeIndex, setActiveIndex] = React.useState(0);

    React.useEffect(() => {
        const handleFocusIn = (event: FocusEvent) => {
            const focusedIndex = missingFields.findIndex(({ element }) =>
                element.matches(".note-error")
                    ? element.previousElementSibling?.contains(
                          event.target as Node
                      )
                    : element.contains(event.target as Node)
            );

            if (focusedIndex !== -1) setActiveIndex(focusedIndex);
        };

        document.addEventListener("focusin", handleFocusIn);

        return () => document.removeEventListener("focusin", handleFocusIn);
    }, [missingFields]);

    if (missingFields.length === 0) return null;

    const currentIndex = Math.min(activeIndex, missingFields.length - 1);
    const hasMultipleFields = missingFields.length > 1;

    const currentField = missingFields[currentIndex];

    const goToField = (index: number) => {
        setActiveIndex(index);
        goToMissingField(missingFields[index].element);
    };

    return (
        <div role="status" className="mr-auto min-w-0">
            <Alert
                type="error"
                classNames="gap-x-4 py-2 shadow-active"
                description={
                    <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        {missingFields.length}{" "}
                        {hasMultipleFields ? "fields need" : "field needs"}{" "}
                        attention before submitting
                        <Button
                            type="button"
                            variant="normal"
                            title={currentField.label}
                            className="min-w-0 max-w-[20rem]"
                            onClick={() => goToField(currentIndex)}
                        >
                            <Badge
                                type="danger"
                                className="normal-case max-w-full
                                    overflow-hidden hover:underline"
                                content={
                                    <>
                                        {hasMultipleFields ? (
                                            <span className="shrink-0">
                                                {currentIndex + 1} of{" "}
                                                {missingFields.length}:&nbsp;
                                            </span>
                                        ) : null}
                                        <span className="min-w-0 truncate">
                                            {currentField.label}
                                        </span>
                                    </>
                                }
                            />
                        </Button>
                    </span>
                }
                actionComponent={
                    hasMultipleFields ? (
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="secondary"
                                aria-label="Previous missing field"
                                disabled={currentIndex === 0}
                                onClick={() => goToField(currentIndex - 1)}
                            >
                                Previous
                            </Button>
                            <Button
                                type="button"
                                variant="secondary"
                                aria-label="Next missing field"
                                disabled={
                                    currentIndex === missingFields.length - 1
                                }
                                onClick={() => goToField(currentIndex + 1)}
                            >
                                Next
                            </Button>
                        </div>
                    ) : null
                }
            />
        </div>
    );
}
